import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import localizedFormat from "dayjs/plugin/localizedFormat";
import relativeTime from "dayjs/plugin/relativeTime";
import timezone from "dayjs/plugin/timezone";
import updateLocale from "dayjs/plugin/updateLocale";
import utc from "dayjs/plugin/utc";

// Türkçe locale
import "dayjs/locale/ar";
import "dayjs/locale/az";
import "dayjs/locale/de";
import "dayjs/locale/en";
import "dayjs/locale/ru";
import "dayjs/locale/tr";

// Plugin'leri yükle
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);
dayjs.extend(localizedFormat);
dayjs.extend(relativeTime);
dayjs.extend(updateLocale);

dayjs.updateLocale("tr", {
  weekdaysShort: [
    "Paz", // 0
    "Pzt", // 1
    "Sal", // 2
    "Çrş", // 3
    "Per", // 4
    "Cum", // 5
    "Cmt", // 6
  ],
});

const durationLocaleLabels = {
  tr: { hourLabel: "s", minuteLabel: "d" },
  en: { hourLabel: "h", minuteLabel: "m" },
  de: { hourLabel: "Std", minuteLabel: "Min" },
  ar: { hourLabel: "س", minuteLabel: "د" },
};

function getDurationLabels(locale = "tr") {
  if (!locale) {
    return durationLocaleLabels.tr;
  }

  const normalizedLocale =
    typeof locale === "string" ? locale.toLowerCase().split("-")[0] : "tr";

  return durationLocaleLabels[normalizedLocale] || durationLocaleLabels.tr;
}

function parseDurationInput(durationInput) {
  if (durationInput === undefined || durationInput === null) {
    return null;
  }

  if (typeof durationInput === "string") {
    const trimmed = durationInput.trim();
    if (!trimmed) {
      return null;
    }

    if (trimmed.includes(":")) {
      const [hoursPart = "0", minutesPart = "0"] = trimmed.split(":");
      const hours = Number.parseInt(hoursPart, 10);
      const minutes = Number.parseInt(minutesPart, 10);

      if (Number.isNaN(hours) || Number.isNaN(minutes)) {
        return null;
      }

      return {
        hours,
        minutes,
      };
    }

    const numericValue = Number(trimmed);
    if (!Number.isNaN(numericValue)) {
      const totalMinutes = Math.round(numericValue);
      return {
        hours: Math.floor(totalMinutes / 60),
        minutes: totalMinutes % 60,
      };
    }

    return null;
  }

  if (typeof durationInput === "number" && Number.isFinite(durationInput)) {
    const totalMinutes = Math.round(durationInput);
    return {
      hours: Math.floor(totalMinutes / 60),
      minutes: totalMinutes % 60,
    };
  }

  return null;
}

export function formatDurationValue(durationInput, locale = "tr") {
  const parsed = parseDurationInput(durationInput);

  if (!parsed) {
    return "";
  }

  const { hours, minutes } = parsed;
  const { hourLabel, minuteLabel } = getDurationLabels(locale);
  const parts = [];

  if (hours > 0) {
    parts.push(`${hours}${hourLabel}`);
  }

  if (minutes > 0 || parts.length === 0) {
    parts.push(`${minutes}${minuteLabel}`);
  }

  return parts.join(" ");
}

/**
 * Tarih formatlaması yapan yardımcı fonksiyon
 * @param {string|Date|dayjs.Dayjs} dateInput - Formatlanacak tarih (ISO 8601, Date object, dayjs object)
 * @param {Object} options - Formatlama seçenekleri
 * @param {string} options.locale - Locale kodu (varsayılan: 'tr')
 * @param {string} options.format - Çıktı formatı (varsayılan: 'DD MMM YYYY')
 * @param {boolean} options.showTime - Saat gösterilsin mi? (varsayılan: false)
 * @param {string} options.timeFormat - Saat formatı (varsayılan: 'HH:mm')
 * @param {string} options.timezone - Timezone (varsayılan: local timezone)
 * @param {boolean} options.relative - Göreceli tarih (2 gün önce) gösterilsin mi? (varsayılan: false)
 * @returns {string} Formatlanmış tarih metni
 */
export function formatDate(dateInput, options = {}) {
  const {
    locale = "tr",
    format = "DD MMM YYYY",
    showTime = false,
    timeFormat = "HH:mm",
    timezone,
    relative = false,
  } = options;

  if (!dateInput) {
    console.warn("formatDate: Geçersiz tarih girişi");
    return "";
  }

  try {
    let date;

    if (typeof dateInput === "string") {
      const s = dateInput.trim();

      // String ise önce bilinen formatlarla STRICT parse dene
      date = dayjs(
        s,
        [
          "DD/MM/YYYY",
          "DD/MM/YYYY HH:mm",
          "DD/MM/YYYY HH:mm:ss",
          "YYYY-MM-DD",
          "YYYY.MM.DD",
          "YYYY-MM-DDTHH:mm:ss",
          "YYYY-MM-DDTHH:mm:ss.SSSZ",
          "YYYY-MM-DDTHH:mm:ssZ",
        ],
        locale,
        true, // strict
      );

      // Olmadıysa, ISO/RFC gibi formatlar için dayjs'in kendi parser'ını dene
      if (!date.isValid()) {
        date = dayjs(s);
      }
    } else {
      // Date / dayjs / timestamp vs.
      date = dayjs(dateInput);
    }

    if (!date.isValid()) {
      console.warn(`formatDate: Geçersiz tarih formatı: ${dateInput}`);
      return typeof dateInput === "string" ? dateInput : dateInput.toString();
    }

    if (timezone) {
      date = date.tz(timezone);
    }

    date = date.locale(locale);

    if (relative) {
      return date.fromNow();
    }

    const finalFormat = showTime ? `${format} ${timeFormat}` : format;
    return date.format(finalFormat);
  } catch (error) {
    console.error("formatDate error:", error);
    return typeof dateInput === "string" ? dateInput : dateInput.toString();
  }
}

/**
 * Hazır formatlar için kolaylaştırıcı fonksiyonlar
 */
export const dateFormats = {
  /**
   * Standart format: 12 Ağu 2025
   */
  standard: (dateInput, locale = "tr") =>
    formatDate(dateInput, { locale, format: "DD MMM YYYY" }),

  /**
   * Kısa format: 12/08/2025
   */
  short: (dateInput, locale = "tr") =>
    formatDate(dateInput, { locale, format: "DD/MM/YYYY" }),

  /**
   * ISO format: 2025-08-12
   */
  iso: (dateInput) => formatDate(dateInput, { format: "YYYY-MM-DD" }),

  /**
   * Uzun format: 12 Ağustos 2025
   */
  long: (dateInput, locale = "tr") =>
    formatDate(dateInput, { locale, format: "DD MMMM YYYY" }),

  /**
   * Saat ile: 12 Ağu 2025 14:30
   */
  withTime: (dateInput, locale = "tr") =>
    formatDate(dateInput, { locale, showTime: true }),

  /**
   * Sadece saat: 14:30
   */
  timeOnly: (dateInput, locale = "tr") =>
    formatDate(dateInput, { locale, format: "HH:mm" }),

  /**
   * Göreceli: 2 gün önce
   */
  relative: (dateInput, locale = "tr") =>
    formatDate(dateInput, { locale, relative: true }),

  /**
   * Süre formatı: 1s 20d (locale'a göre)
   */
  duration: (durationInput, locale = "tr") =>
    formatDurationValue(durationInput, locale),

  /**
   * Gün + tarih: Salı, 12 Ağu 2025
   */
  withDay: (dateInput, locale = "tr") =>
    formatDate(dateInput, { locale, format: "dddd, DD MMM YYYY" }),

  /**
   * API friendly: 2025-08-12T14:30:00
   */
  apiFormat: (dateInput) =>
    formatDate(dateInput, { format: "YYYY-MM-DDTHH:mm:ss" }),

  /**
   * Gün ve kısa ay: 15 Oca
   */
  dayShortMonth: (dateInput, locale = "tr") =>
    formatDate(dateInput, { locale, format: "DD MMM" }),

  /**
   * Gün, uzun ay ve kısa gün adı: 13 Ekim Cum
   */
  dayLongMonthShortDay: (dateInput, locale = "tr") =>
    formatDate(dateInput, { locale, format: "DD MMMM ddd" }),

  /**
   * Gün, kısa ay ve kısa gün adı: 25 Kas Sal
   */
  dayShortMonthShortDay: (dateInput, locale = "tr") =>
    formatDate(dateInput, { locale, format: "DD MMM ddd" }),

  /**
   * Gün, kısa ay ve virgüllü kısa gün adı: 25 Kas, Sal
   */
  dayShortMonthShortDayWithComma: (dateInput, locale = "tr") =>
    formatDate(dateInput, { locale, format: "DD MMM, ddd" }),
};

/**
 * Flight duration string'ini normalize eder - ilk iki değeri alır ve negatif işaretleri kaldırır
 * Örnek: "1s 30d" → "1s 30d"
 * Örnek: "1D -22s 15d" → "1D 22s"
 * @param {string} durationString - Flight duration string'i
 * @returns {string} - Normalize edilmiş duration string'i (ilk iki değer, negatif işaretler kaldırılmış)
 */
export function normalizeFlightDuration(durationString) {
  if (!durationString || typeof durationString !== "string") {
    return durationString || "";
  }

  // Boşluklara göre split et ve boş string'leri filtrele
  const parts = durationString
    .trim()
    .split(/\s+/)
    .filter((part) => part.length > 0);

  // İlk iki parçayı al
  if (parts.length === 0) {
    return "";
  }

  // İlk iki parçayı al ve her birinden başındaki "-" işaretini kaldır
  const normalizedParts = parts
    .slice(0, 2)
    .map((part) => (part.startsWith("-") ? part.substring(1) : part));

  return normalizedParts.join(" ");
}

/**
 * Tarih aralığını formatla
 * @param {string|Date} startDate - Başlangıç tarihi
 * @param {string|Date} endDate - Bitiş tarihi
 * @param {Object} options - Formatlama seçenekleri
 * @returns {string} Formatlanmış tarih aralığı
 */
export function formatDateRange(startDate, endDate, options = {}) {
  const { locale = "tr", separator = " - " } = options;

  const start = formatDate(startDate, { locale, ...options });
  const end = formatDate(endDate, { locale, ...options });

  return `${start}${separator}${end}`;
}

/**
 * Uçuş tarihlerine özel formatter
 * @param {string} departureDate - Gidiş tarihi
 * @param {string} returnDate - Dönüş tarihi (opsiyonel)
 * @param {Object} options - Formatlama seçenekleri
 * @returns {Object} Formatlanmış uçuş tarihleri
 */
export function formatFlightDates(
  departureDate,
  returnDate = null,
  options = {},
) {
  const { locale = "tr", showTime = false } = options;

  const departure = formatDate(departureDate, {
    locale,
    showTime,
    format: "DD MMM YYYY",
  });

  if (!returnDate) {
    return {
      departure,
      return: null,
      summary: departure,
      isRoundTrip: false,
    };
  }

  const returnFormatted = formatDate(returnDate, {
    locale,
    showTime,
    format: "DD MMM YYYY",
  });

  return {
    departure,
    return: returnFormatted,
    summary: `${departure} - ${returnFormatted}`,
    isRoundTrip: true,
  };
}

/**
 * Yaş hesaplama
 * @param {string|Date} birthDate - Doğum tarihi
 * @returns {number} Yaş
 */
export function calculateAge(birthDate) {
  if (!birthDate) return 0;

  const birth = dayjs(birthDate);
  const now = dayjs();

  if (!birth.isValid()) {
    console.warn(`calculateAge: Geçersiz doğum tarihi: ${birthDate}`);
    return 0;
  }

  return now.diff(birth, "year");
}

/**
 * Tarih geçerliliğini kontrol et
 * @param {string|Date} dateInput - Kontrol edilecek tarih
 * @returns {boolean} Geçerli mi?
 */
export function isValidDate(dateInput) {
  if (!dateInput) return false;
  return dayjs(dateInput).isValid();
}

/**
 * İki tarih arasındaki farkı hesapla
 * @param {string|Date} date1 - İlk tarih
 * @param {string|Date} date2 - İkinci tarih
 * @param {string} unit - Birim (day, hour, minute, etc.)
 * @returns {number} Fark
 */
export function dateDifference(date1, date2, unit = "day") {
  const d1 = dayjs(date1);
  const d2 = dayjs(date2);

  if (!d1.isValid() || !d2.isValid()) {
    console.warn("dateDifference: Geçersiz tarih(ler)");
    return 0;
  }

  return d2.diff(d1, unit);
}

/**
 * İki tarihin aynı gün olup olmadığını kontrol et
 * @param {string|Date} date1 - İlk tarih
 * @param {string|Date} date2 - İkinci tarih
 * @returns {boolean} Aynı gün ise true
 */
export function isSameDay(date1, date2) {
  if (!date1 || !date2) return false;

  const d1 = dayjs(date1);
  const d2 = dayjs(date2);

  if (!d1.isValid() || !d2.isValid()) {
    return false;
  }

  return d1.format("YYYY-MM-DD") === d2.format("YYYY-MM-DD");
}

/**
 * İki tarihin hangisinin daha önce olduğunu kontrol et
 * @param {string|Date} date1 - İlk tarih
 * @param {string|Date} date2 - İkinci tarih
 * @returns {boolean} date1, date2'den önce ise true
 */
export function isBefore(date1, date2) {
  if (!date1 || !date2) return false;

  const d1 = dayjs(date1);
  const d2 = dayjs(date2);

  if (!d1.isValid() || !d2.isValid()) {
    return false;
  }

  return d1.isBefore(d2, "day");
}

/**
 * İki tarihin hangisinin daha sonra olduğunu kontrol et
 * @param {string|Date} date1 - İlk tarih
 * @param {string|Date} date2 - İkinci tarih
 * @returns {boolean} date1, date2'den sonra ise true
 */
export function isAfter(date1, date2) {
  if (!date1 || !date2) return false;

  const d1 = dayjs(date1);
  const d2 = dayjs(date2);

  if (!d1.isValid() || !d2.isValid()) {
    return false;
  }

  return d1.isAfter(d2, "day");
}

/**
 * Verilen tarihin geçip geçmediğini kontrol et (şu anın öncesinde mi).
 * "2026-04-16T05:45:00" gibi ISO formatını destekler; saat/dakika dahil karşılaştırır.
 * @param {string|Date} dateInput - Kontrol edilecek tarih (örn. "2026-04-16T05:45:00")
 * @returns {boolean} Tarih geçmişteyse true, geçmemişse veya geçersizse false
 */
export function isPassed(dateInput) {
  if (!dateInput) return false;

  const d = dayjs(dateInput);
  if (!d.isValid()) return false;

  return d.isBefore(dayjs());
}

/**
 * Başlangıç ve bitiş tarihi aralığını parse edip özet JSON döner.
 * Format: "2026-01-01T00:00:00"
 *
 * - Aynı gün: { date: "1 Ocak 2026", length: 1, dayIndexes: [4] }
 * - Farklı günler: { date: "01-02 Ocak 2026", length: 2, dayIndexes: [4, 5] }
 *
 * @param {string} startDate - Başlangıç ("2026-01-01T00:00:00")
 * @param {string} endDate - Bitiş ("2026-01-02T00:00:00"); start ile aynı olabilir
 * @param {Object} options - { locale?: string }
 * @returns {{ date: string, length: number, dayIndexes: number[] }}
 */
export function formatDateRangeSummary(startDate, endDate, options = {}) {
  const { locale = "tr" } = options;

  const start = dayjs(startDate, ["YYYY-MM-DD", "YYYY-MM-DDTHH:mm:ss"], true);
  const end = dayjs(endDate, ["YYYY-MM-DD", "YYYY-MM-DDTHH:mm:ss"], true);

  if (!start.isValid() || !end.isValid()) {
    return { date: "", length: 0, dayIndexes: [] };
  }

  const startNorm = start.startOf("day");
  const endNorm = end.startOf("day");

  if (endNorm.isBefore(startNorm)) {
    return { date: "", length: 0, dayIndexes: [] };
  }

  const length = endNorm.diff(startNorm, "day") + 1;
  const dayIndexes = [];
  let cursor = startNorm;

  while (!cursor.isAfter(endNorm)) {
    dayIndexes.push(cursor.day()); // 0 Pazar, 6 Cumartesi
    cursor = cursor.add(1, "day");
  }

  let dateStr;

  if (length === 1) {
    dateStr = formatDate(startDate, {
      locale,
      format: "D MMMM YYYY",
    });
  } else {
    const sameMonth = startNorm.month() === endNorm.month();
    const sameYear = startNorm.year() === endNorm.year();

    if (sameMonth && sameYear) {
      const startFormatted = startNorm.locale(locale).format("DD");
      const endFormatted = endNorm.locale(locale).format("DD");
      const monthYear = startNorm.locale(locale).format("MMMM YYYY");
      dateStr = `${startFormatted}-${endFormatted} ${monthYear}`;
    } else {
      const startPart = formatDate(startDate, { locale, format: "DD MMM" });
      const endPart = formatDate(endDate, { locale, format: "DD MMM YYYY" });
      dateStr = `${startPart} - ${endPart}`;
    }
  }

  return {
    date: dateStr,
    length,
    dayIndexes,
  };
}

/**
 * Tarihi relative label'a çevirir (today, tomorrow, thisWeek, ...).
 * Geçersiz veya boş tarih durumunda boş string döner.
 * @param {string|Date} isoDateString
 * @returns {string}
 */
export function getRelativeDateLabel(isoDateString) {
  if (!isoDateString) return "";

  const target = new Date(isoDateString);
  if (Number.isNaN(target.getTime())) return "";

  const today = new Date();
  const startOfToday = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );
  const startOfTarget = new Date(
    target.getFullYear(),
    target.getMonth(),
    target.getDate(),
  );

  const diffDays = Math.round(
    (startOfTarget.getTime() - startOfToday.getTime()) / (1000 * 60 * 60 * 24),
  );

  if (diffDays === 0) return "today"; // Bugün
  if (diffDays === 1) return "tomorrow"; // Yarın
  if (diffDays > 1 && diffDays <= 7) return "thisWeek"; // Bu Hafta
  if (diffDays > 7 && diffDays <= 14) return "nextWeek"; // Gelecek Hafta
  if (diffDays > 14 && diffDays <= 21) return "inTwoWeeks"; // 2 Hafta Sonra
  if (diffDays > 21 && diffDays <= 31) return "nextMonth"; // Gelecek Ay

  return "";
}

/**
 * Ay indexi (0-11) verilerek o ayın kısa ismini döner
 * @param {number} monthIndex - Ay indexi (0=Ocak, 1=Şubat, ..., 11=Aralık)
 * @param {string} locale - Locale kodu (varsayılan: 'tr')
 * @returns {string} Ayın kısa ismi (Oca, Şub, Mar, vb.)
 */
export function getShortMonthName(monthIndex, locale = "tr") {
  if (monthIndex < 0 || monthIndex > 11 || !Number.isInteger(monthIndex)) {
    console.warn(
      `getShortMonthName: Geçersiz ay indexi: ${monthIndex}. 0-11 arası olmalı.`,
    );
    return "";
  }

  try {
    // Ayın ilk gününü oluştur (herhangi bir yıl olabilir)
    const date = dayjs().month(monthIndex).locale(locale);
    return date.format("MMM");
  } catch (error) {
    console.error("getShortMonthName error:", error);
    return "";
  }
}

/**
 * Ay indexi (0-11) verilerek o ayın tam ismini döner
 * @param {number} monthIndex - Ay indexi (0=Ocak, 1=Şubat, ..., 11=Aralık)
 * @param {string} locale - Locale kodu (varsayılan: 'tr')
 * @returns {string} Ayın tam ismi (Ocak, Şubat, Mart, vb.)
 */
export function getFullMonthName(monthIndex, locale = "tr") {
  if (monthIndex < 0 || monthIndex > 11 || !Number.isInteger(monthIndex)) {
    console.warn(
      `getFullMonthName: Geçersiz ay indexi: ${monthIndex}. 0-11 arası olmalı.`,
    );
    return "";
  }

  try {
    // Ayın ilk gününü oluştur (herhangi bir yıl olabilir)
    const date = dayjs().month(monthIndex).locale(locale);
    return date.format("MMMM");
  } catch (error) {
    console.error("getFullMonthName error:", error);
    return "";
  }
}

/**
 * Pazartesi'den Pazar'a kadar kısa gün isimlerini array olarak döner
 * @param {string} locale - Locale kodu (varsayılan: 'tr')
 * @param {string} template - gün formatı (varsayılan: ddd)
 * @returns {string[]} Kısa gün isimleri [Pzt, Sal, Çar, Per, Cum, Cmt, Paz]
 */
export function getShortDayNames(locale = "tr", template = "ddd") {
  try {
    const days = [];

    // 2025-01-06 Pazartesi - sabit bir Pazartesi tarihi
    const monday = dayjs("2025-01-06").locale(locale);

    for (let i = 0; i < 7; i++) {
      days.push(monday.add(i, "day").format(template || "ddd"));
    }

    return days;
  } catch (error) {
    console.error("getShortDayNames error:", error);
    return [];
  }
}
